import { useMemo } from 'react';

import TextDisplayBlock from '@apigrowfinance/pbolt-text-display-block';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';

import { ITimelineItem } from '@clients/timelineClient';
import accountTimelineCategoryDesc from '@messages/accountTimelineCategoryDesc';
import getEnumTranslation from '@utils/getEnumTranslation';
import { toTitleCase } from '@utils/string/caseUtils';

interface TimelineDetailsBodyCardProps {
    timelineItem: ITimelineItem;
    isLoading?: boolean;
}

const TimelineDetailsBodyCashIn = ({
    timelineItem,
    isLoading,
}: TimelineDetailsBodyCardProps) => {
    const { formatMessage } = useIntl();

    const textBlocks = useMemo(() => {
        return [
            {
                label: formatMessage({
                    id: 'TimelineDetails.description',
                    defaultMessage: 'Description',
                }),
                value: timelineItem?.category
                    ? (getEnumTranslation(timelineItem?.category + '_DESC', {
                          messages: accountTimelineCategoryDesc,
                          formatMessage,
                      }) as string)
                    : '',
                width: 12,
            },
            {
                label: ' ',
                value: ' ',
                width: 12,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.type',
                    defaultMessage: 'Type',
                }),
                value: toTitleCase(timelineItem?.type),
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.category',
                    defaultMessage: 'Category',
                }),
                value: toTitleCase(timelineItem?.category),
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.contractAmount',
                    defaultMessage: 'Contract Amount',
                }),
                value: toTitleCase(timelineItem?.data?.item?.contract_amount),
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.responseCode',
                    defaultMessage: 'Response Code',
                }),
                value: toTitleCase(timelineItem?.data?.item?.response_code),
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.amount',
                    defaultMessage: 'Amount',
                }),
                value: toTitleCase(timelineItem?.data?.item?.amount),
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.toName',
                    defaultMessage: 'To Name',
                }),
                value: toTitleCase(timelineItem?.data?.item?.to_name),
            },

            {
                label: formatMessage({
                    id: 'TimelineDetails.numberOfInstallments',
                    defaultMessage: 'Number of Installments',
                }),
                value: toTitleCase(
                    timelineItem?.data?.item?.org_operation
                        ?.number_of_installments,
                ),
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.principalAmount',
                    defaultMessage: 'Principal Amount',
                }),
                value: toTitleCase(timelineItem?.data?.item?.principal_amount),
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.cardholderbillingAmount',
                    defaultMessage: 'Cardholder Billing Amount',
                }),
                value: toTitleCase(
                    timelineItem?.data?.item?.cardholderbilling_amount,
                ),
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.currencyCode',
                    defaultMessage: 'Currency Code',
                }),
                value: toTitleCase(timelineItem?.data?.item?.currency_code),
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.preAuthorization',
                    defaultMessage: 'Pre-authorization',
                }),
                value: toTitleCase(timelineItem?.data?.item?.pre_authorization),
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.softDescriptor',
                    defaultMessage: 'Soft Descriptor',
                }),
                value: toTitleCase(timelineItem?.data?.item?.soft_descriptor),
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.localAmount',
                    defaultMessage: 'Local Amount',
                }),
                value: toTitleCase(timelineItem?.data?.item?.local_amount),
            },
            {
                label: formatMessage({
                    id: 'id',
                    defaultMessage: 'Id',
                }),
                value: timelineItem?.data?.item?.id,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.latitude',
                    defaultMessage: 'Latitude',
                }),
                value: timelineItem?.data?.item?.latitude,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.longitude',
                    defaultMessage: 'Longitude',
                }),
                value: timelineItem?.data?.item?.longitude,
            },
            {
                label: ' ',
                value: ' ',
                width: 12,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.correlationId',
                    defaultMessage: 'Correlation Id',
                }),
                value: timelineItem?.correlation_id,
                width: 6,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.timestamp',
                    defaultMessage: 'Timestamp',
                }),
                value: dayjs(timelineItem?.timestamp).format(
                    'D MMM YYYY h:mmA',
                ),
                width: 6,
            },
        ];
    }, [timelineItem, formatMessage]);

    return <TextDisplayBlock isLoading={isLoading} textBlocks={textBlocks} />;
};

export default TimelineDetailsBodyCashIn;
